'use client';
import { useEffect, useState, FormEventHandler } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { AutoCompleteAddress, CustomFormData } from 'shared/types/account/Account';
import LockIcon from '@/components/customercenter-ui/icons/lock';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import ToolTips from '@/components/customercenter-ui/tooltip';
import AddressLineTooltip from '@/components/customercenter-ui/tooltip/address';
import PhoneNumberTooltip from '@/components/customercenter-ui/tooltip/phoneNumber';
import { useMutuallyExclusiveDobSsn } from '@/components/customercenter-ui/utils/useMutuallyExclusiveDobSsn';
import { useAccount } from '@/frontastic';
import useIntuitiveSearch from '@/frontastic/hooks/useIntuitiveSearch';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { compareAddresses, getAgentName, TranslateText } from '@/helpers/utils/siteUtils';
import {
  UpdateCustomerDetialsPayload,
  ValidateAddress,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { CONSTANTS_RESPONSE, DEFAULT_SITE_LANG, FORM_FIELD_SPECS } from '../../../../../constants/constant';
import AddCircle from '../../../svgs/addCircle';
import {
  CUSTOMER_DETAILS_RECORD_KEYS,
  DEFAULT_COUNTRY,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../customer-detail/constants';
import { PageTexts } from '../../customer-detail/i18n';
import {
  customerDetailsSchema,
  customerNoCreditSchema,
  customerDetailsSSNSchema,
  customerDetailsDOBSchema,
  customerDetailsNoSSNSchema,
} from '../../customer-detail/schema';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import InputField from '../../ui/customInput/input';
import { StateDropDown } from '../../ui/customSelect/stateDropDown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';
import { AddressDialogModal } from '../common/validateAddress';

const EditCustomerDetailsModal = ({
  updateAccountAddress,
  formData,
  open,
  setOpen,
  creditAccountId,
  isAllowedSite,
}: {
  updateAccountAddress: (payload: any) => void;
  formData: CustomFormData;
  open: boolean;
  setOpen: (value: boolean) => void;
  creditAccountId: string;
  isAllowedSite: boolean;
}) => {
  const { toast } = useToast();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const params = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [initialStateValue, setInitialStateValue] = useState<string | undefined>('');
  const [pickListSelectedAddress, setPickListSelectedAddress] = useState<AutoCompleteAddress>();
  const { updateCustomerDetails, validateAddress } = useAccount();
  const [modalOpen, setModalOpen] = useState(false);
  const [formattingKey, setFormattingKey] = useState('');
  const [validateAddressResponse, setValidateAddressResponse] = useState({} as any);
  const handleShowadditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };

  let schema;
  if (creditAccountId) {
    schema = customerDetailsSchema;
  } else if (formData.ssn && formData.dob) {
    schema = customerDetailsSchema;
  } else if (formData.dob) {
    schema = customerDetailsDOBSchema;
  } else if (formData.ssn) {
    schema = customerDetailsSSNSchema;
  } else if (!formData.ssn && !formData.dob) {
    schema = customerDetailsNoSSNSchema;
  } else {
    schema = customerNoCreditSchema;
  }

  // Initialize useForm with the selected schema
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    control,
    reset,
    clearErrors,
    getValues,
    trigger,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      ...formData,
    },
  });

  const firstName = watch('firstName') || '';
  const lastName = watch('lastName') || '';

  const combinedNameWarning = firstName?.length + lastName?.length >= 28 ? 'Character Limit Reached' : '';

  const { disableDob, disableSsn } = useMutuallyExclusiveDobSsn({
    control,
    dobName: FORM_FIELD_SPECS.dateOfBirth.id,
    ssnName: FORM_FIELD_SPECS.ssn.id,
    originalDob: formData?.dob ?? '',
    originalSsn: formData?.ssn ?? '',
    isActive: isAllowedSite,
  });

  useIntuitiveSearch({
    setShowadditionalAddressInfo,
    setValue,
    setInitialStateValue,
    clearErrors,
    setPickListSelectedAddress,
  });

  useEffect(() => {
    setInitialStateValue(formData?.state);
  }, []);

  const saveAddress = async (formData: CustomFormData) => {
    const data = formData;
    setLoading(true);
    updateAccountAddress(data);
    const BillingLocationInfo = {
      LocationInfo: {
        line1: data?.streetName?.trim(),
        line2: data?.additionalAddressInfo?.trim(),
        city: data?.city?.trim(),
        state: data?.state,
        zip: data?.postalCode,
        country: DEFAULT_COUNTRY,
        mailWasReturned: data?.mailReturnUndeliverable,
      },
    };
    const payload = {
      query: {
        customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        agentName: getAgentName()?.replace(/@.*/, ''),
      },
      data: {
        CustomerInfo: {
          firstName: data?.firstName?.trim(),
          lastName: data?.lastName?.trim(),
          ...(data?.ssn ? { ssn: data?.ssn } : {}),
          ...(data?.dob ? { dob: data?.dob } : {}),
          companyName: data?.companyName?.trim(),
        },
        BillingAddress: BillingLocationInfo,
      },
    } as UpdateCustomerDetialsPayload;

    await updateCustomerDetails(payload)
      .then((res) => {
        setLoading(false);
        if (res?.code === RESPONSE_SUCCESS_MESSAGE) {
          setOpen(false);
          SuccessNotification(toast, { description: res?.message, action: <SuccessIcon className="h-5 w-5" /> });
        } else {
          ErrorNotification(toast, {
            description: Texts.updateCustomerDetailsError,
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onSubmit = async (data: CustomFormData) => {
    updateAccountAddress(data);
    saveAddress(data);
  };

  const mergeAddressFromExactMatch = (locationInfo: any, formValues: any) => {
    const formData = { ...formValues };
    formData.streetName = locationInfo?.line1;
    formData.additionalAddressInfo = locationInfo?.line2;
    formData.city = locationInfo?.city;
    formData.state = locationInfo?.state;
    formData.postalCode = locationInfo?.zip;
    onSubmit(formData);
  };

  const verifyAddress = async () => {
    setLoading(true);
    const formValues: any = getValues();
    const addressDiffers = compareAddresses(formValues, pickListSelectedAddress);
    const formData = {
      OriginalAddress: {
        LocationInfo: {
          line1: formValues?.streetName,
          line2: formValues?.additionalAddressInfo,
          city: formValues?.city,
          state: formValues?.state,
          zip: formValues?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    if (addressDiffers) {
      const payload = {
        data: formData,
        query: {
          formattedKey: formattingKey,
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
      } as ValidateAddress;
      const response: any = await validateAddress(payload);

      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, formValues);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(!modalOpen);
          setValidateAddressResponse(validateAddressResponse);
        }
      } else {
        handleSubmit(onSubmit)(formValues);
      }
    } else {
      handleSubmit(onSubmit)(formValues);
    }
    setLoading(false);
  };

  const mergeDataWithFormData = async (data: any) => {
    const formData = getValues();
    const mergedData: any = { ...formData, ...data };
    const EditformData = {
      OriginalAddress: {
        LocationInfo: {
          line1: mergedData?.streetName,
          line2: mergedData?.additionalAddressInfo,
          city: mergedData?.city,
          state: mergedData?.state,
          zip: mergedData?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    const payload = {
      data: EditformData,
      query: {
        formattedKey: formattingKey,
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
    } as ValidateAddress;

    if (data?.isEditSelectedFromModel) {
      const response: any = await validateAddress(payload);
      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, mergedData);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(true);
          setValidateAddressResponse(validateAddressResponse);
          Object.keys(mergedData).forEach((fieldName: any) => {
            setValue(fieldName, mergedData[fieldName]);
          });
        }
      } else {
        onSubmit(mergedData);
      }
    } else {
      onSubmit(mergedData);
    }
  };
  const handleInput: FormEventHandler<HTMLFormElement> = (event) => {
    const input = event.currentTarget;
    const newValue = input?.value?.replace(/[^a-zA-Z0-9\s'-]/g, '');
    if (input?.value !== newValue) {
      input.value = newValue;
      const changeEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          reset(formData);
        }
      }}
    >
      <DialogContent className="custom-scrollbar max-h-[90%] max-w-[684px] overflow-y-auto bg-white sm:rounded-md">
        {loading && <Loader />}
        {modalOpen && (
          <AddressDialogModal
            openModal={modalOpen}
            setOpenModal={setModalOpen}
            values={getValues()}
            validateAddressResponse={validateAddressResponse}
            mergeDataWithFormData={mergeDataWithFormData}
            setFormattingKey={setFormattingKey}
            Texts={Texts}
          />
        )}
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.editCustomerDetailsButton}</div>
            <div className="text-xs font-normal">*{Texts.requiredFields}</div>
          </DialogTitle>
        </DialogHeader>
        <section>
          <form onSubmit={handleSubmit(verifyAddress)} autoComplete="off">
            <section className="flex w-full items-baseline gap-2 px-1 pt-5">
              <InputField
                items={Texts.formItemFirstName}
                id={FORM_FIELD_SPECS.firstName.id}
                htmlFor={FORM_FIELD_SPECS.firstName.id}
                register={register}
                errors={{
                  ...errors,
                  ...(combinedNameWarning &&
                    !errors?.firstName && {
                      firstName: {
                        message: combinedNameWarning,
                      },
                    }),
                }}
                label={Texts.formFirstNameLabel}
                classname="w-[297px]"
                maxLength={FORM_FIELD_SPECS.firstName.maxLength}
                focusFirstName={true}
                onChange={handleInput}
                rules={{
                  onChange: () => {
                    if (getValues('lastName')) {
                      trigger(['firstName', 'lastName']);
                    }
                  },
                }}
                getValues={getValues}
              />
              <InputField
                items={Texts.formItemLastName}
                id={FORM_FIELD_SPECS.lastName.id}
                htmlFor={FORM_FIELD_SPECS.lastName.id}
                register={register}
                errors={{
                  ...errors,
                  ...(combinedNameWarning &&
                    !errors?.lastName && {
                      lastName: {
                        message: combinedNameWarning,
                      },
                    }),
                }}
                label={Texts.formLastNameLabel}
                classname="w-[297px]"
                maxLength={FORM_FIELD_SPECS.lastName.maxLength}
                onChange={handleInput}
                rules={{
                  onChange: () => {
                    if (getValues('firstName')) {
                      trigger(['firstName', 'lastName']);
                    }
                  },
                }}
                getValues={getValues}
              />
            </section>
            {!showCompany && (
              <button
                type="button"
                className="flex items-center gap-x-2 px-1 pt-6"
                onClick={() => setShowCompany(true)}
              >
                <AddCircle className="h-5 w-5" />
                <p className="text-base font-normal leading-5">{Texts.formItemCompanyName}</p>
                <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
              </button>
            )}
            {showCompany && (
              <section className="flex w-full items-baseline px-1 pt-6">
                <section className="w-full lg:max-w-[580px]">
                  <InputField
                    items={Texts.formItemCompanyName}
                    id={FORM_FIELD_SPECS.companyName.id}
                    htmlFor={FORM_FIELD_SPECS.companyName.id}
                    register={register}
                    errors={errors}
                    label={Texts.formCompanyNameLabel}
                    classname="w-full"
                    maxLength={FORM_FIELD_SPECS.companyName.maxLength}
                  />
                </section>
              </section>
            )}
            <section className="flex w-full items-baseline gap-x-2 px-1 pt-6">
              <section className="w-full cursor-pointer lg:max-w-[580px]">
                <InputField
                  items={Texts.formItemAddress}
                  id={FORM_FIELD_SPECS.addressLine1.id}
                  htmlFor={FORM_FIELD_SPECS.addressLine1.id}
                  register={register}
                  errors={errors}
                  label={FORM_FIELD_SPECS.addressLine1.id}
                  classname="w-full address-input"
                  maxLength={FORM_FIELD_SPECS.addressLine1.maxLength}
                />
              </section>
              <ToolTips Content={{ ContentToolTip: <AddressLineTooltip /> }} />
            </section>
            {!showadditionalAddressInfo && (
              <button
                type="button"
                className="flex items-center gap-x-2 px-1 pt-6"
                onClick={handleShowadditionalAddressInfo}
              >
                <AddCircle className="h-5 w-5" />
                <p className="text-base font-normal leading-5">{Texts.formItemAdditionalAddressInfo}</p>
                <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
              </button>
            )}
            <section
              className={`flex w-full items-baseline px-1 pt-6 ${showadditionalAddressInfo ? 'block' : 'hidden'}`}
            >
              <section className="w-full lg:max-w-[580px]">
                <InputField
                  items={Texts.formItemAdditionalAddressInfo}
                  id={FORM_FIELD_SPECS.additionalAddressInfo.id}
                  htmlFor={FORM_FIELD_SPECS.additionalAddressInfo.id}
                  register={register}
                  errors={errors}
                  label={FORM_FIELD_SPECS.additionalAddressInfo.id}
                  maxLength={FORM_FIELD_SPECS.additionalAddressInfo.maxLength}
                  classname="w-full addressLine2"
                />
              </section>
            </section>
            <section className="flex w-full items-baseline gap-4 px-1 pt-6">
              <section className="w-full lg:max-w-[297px]">
                <InputField
                  items={Texts.formItemCity}
                  id={FORM_FIELD_SPECS.city.id}
                  htmlFor={FORM_FIELD_SPECS.city.id}
                  register={register}
                  errors={errors}
                  label={FORM_FIELD_SPECS.city.id}
                  classname="w-full city"
                  maxLength={FORM_FIELD_SPECS.city.maxLength}
                />
              </section>
              <section className="mb-3 w-full lg:max-w-[297px]">
                <StateDropDown
                  setValue={setValue}
                  errors={errors}
                  label={FORM_FIELD_SPECS.state.id}
                  classname="w-full state"
                  initialState={initialStateValue}
                  register={register}
                  id={FORM_FIELD_SPECS.state.id}
                />
              </section>
            </section>
            <section className="flex items-baseline gap-x-4 px-1 pt-6 lg:w-full">
              <section className="mt-2 w-full lg:max-w-[297px]">
                <InputField
                  items={Texts.formItemZipCode}
                  id={FORM_FIELD_SPECS.postalCode.id}
                  htmlFor={FORM_FIELD_SPECS.postalCode.id}
                  register={register}
                  errors={errors}
                  label={FORM_FIELD_SPECS.postalCode.id}
                  classname="w-[297px] zip"
                  maxLength={FORM_FIELD_SPECS.postalCode.maxLength}
                />
              </section>
            </section>
            <section className="flex w-full items-baseline gap-4 px-1 pt-6">
              <section className="flex w-full items-center lg:max-w-[290px]">
                <InputField
                  items={Texts.formItemPhoneNumber}
                  id={FORM_FIELD_SPECS.phone.id}
                  htmlFor={FORM_FIELD_SPECS.phone.id}
                  register={register}
                  errors={errors}
                  label={Texts.formPhoneNumberLabel}
                  classname="w-full"
                  disabled={true}
                />
                <ToolTips Content={{ ContentToolTip: <PhoneNumberTooltip /> }} />
              </section>

              <section className="w-full lg:max-w-[297px]">
                <InputField
                  items={Texts.formItemEmailAddress}
                  id={FORM_FIELD_SPECS.emailAddress.id}
                  htmlFor={FORM_FIELD_SPECS.emailAddress.id}
                  register={register}
                  errors={errors}
                  label={Texts.formEmailAddressLabel}
                  classname="w-full"
                  disabled
                  maxLength={FORM_FIELD_SPECS.emailAddress.maxLength}
                />
              </section>
            </section>
            <section className={`flex w-full items-baseline px-1 pt-6 ${isAllowedSite ? 'block' : 'hidden'}`}>
              <section className="mr-10 flex w-full items-center lg:max-w-[290px]">
                <InputField
                  items={creditAccountId ? Texts.formItemDateOfBirth : Texts.formItemDateOfBirthOptional}
                  id={FORM_FIELD_SPECS.dateOfBirth.id}
                  htmlFor={FORM_FIELD_SPECS.dateOfBirth.id}
                  register={register}
                  errors={errors}
                  label={Texts.formDateOfBirthLabel}
                  disabled={disableDob}
                  classname="w-[290px] cursor-text"
                  type="date"
                />
              </section>
              <section className="flex w-full items-baseline lg:max-w-[400px]">
                <p className="flex-nowrap pb-2 pl-2">XXX - XX - </p>
                <div className="flex flex-nowrap items-center">
                  <InputField
                    items={creditAccountId ? Texts.formItemSsn : Texts.formItemSsnOptional}
                    id={FORM_FIELD_SPECS.ssn.id}
                    htmlFor={FORM_FIELD_SPECS.ssn.id}
                    register={register}
                    errors={errors}
                    label={Texts.formSsnLabel}
                    disabled={disableSsn}
                    classname="w-[170px]"
                    isNum
                    maxLength={FORM_FIELD_SPECS.ssn.maxLength}
                  />
                  <section>
                    <LockIcon className="ml-1 mt-1 h-[24px] w-[24px] fill-black" />
                  </section>
                </div>
              </section>
              <section className="hidden">
                <InputField
                  id={FORM_FIELD_SPECS.country.id}
                  htmlFor={FORM_FIELD_SPECS.country.id}
                  register={register}
                  errors={errors}
                  label={FORM_FIELD_SPECS.country.id}
                  classname="w-full country"
                  value={FORM_FIELD_SPECS.country.default}
                  type={FORM_FIELD_SPECS.country.type}
                />
              </section>
            </section>
            <section className="mt-4">
              <Controller
                name={Texts.formMailReturnUndeliverable as any}
                control={control}
                defaultValue={formData.mailReturnUndeliverable}
                render={({ field }) => (
                  <Checkbox
                    id={CUSTOMER_DETAILS_RECORD_KEYS.MAIL_RETURN_UNDELIVERABLE}
                    defaultChecked={formData.mailReturnUndeliverable}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <label
                htmlFor={CUSTOMER_DETAILS_RECORD_KEYS.MAIL_RETURN_UNDELIVERABLE}
                className="ml-2 text-base font-normal"
              >
                {Texts.formMailReturnLabel}
              </label>
            </section>

            <section className="mt-4 flex justify-start gap-4">
              <Button className="rounded-md bg-deepBlue p-4 text-white">{Texts.saveButton}</Button>
              <Button
                type="reset"
                className="p-0 underline"
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
              >
                {Texts.cancelButton}
              </Button>
            </section>
          </form>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default EditCustomerDetailsModal;
